import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/global.css';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { getStreakCalendar } from './api/quiz.api';

// Public pages
import Landing    from './pages/public/Landing';
import Auth       from './pages/public/Auth';
import ForgotPassword from './pages/public/ForgotPassword';

// Admin pages
import AdminLogin   from './pages/admin/AdminLogin';
import KanaManager  from './pages/admin/KanaManager';
import {
  Overview, VocabularyManager, KanjiManager,
  QuizManager, UsersManager, Analytics, AnnouncementsManager,
} from './pages/admin/AdminPages';

// App pages
import Kana from './pages/app/Kana';
import { Dashboard as Profile, Flashcards, Quiz, Kanji } from './pages/app/AppPages';
import GrammarChapters from './pages/app/GrammarChapters';
import GrammarChapter from './pages/app/GrammarChapter';
import GrammarChapterManager from './pages/admin/GrammarChapterManager';
import SiteSettings from './pages/admin/SiteSettings';
import LearnPath from './pages/app/LearnPath';
import PracticeTests from './pages/app/PracticeTests';
import PracticeTestTake from './pages/app/PracticeTestTake';
import PracticeTestResults from './pages/app/PracticeTestResults';
import PracticeTestHistory from './pages/app/PracticeTestHistory';
import PracticeTestManager from './pages/admin/PracticeTestManager';

// Layouts
import AppSidebar      from './components/app/AppSidebar';
import AppTopbar, { AppLevelProvider } from './components/app/AppTopbar';
import AnnouncementBell from './components/app/AnnouncementBell';
import { ConfigProvider } from './context/ConfigContext';
import PageGate from './components/app/PageGate';
import ErrorBoundary from './components/shared/ErrorBoundary';
import AccountSettings from './pages/app/AccountSettings';
import AdminSidebar    from './components/admin/AdminSidebar';
import MobileBottomNav from './components/app/MobileBottomNav';
import StreakPill      from './components/app/StreakPill';
import StreakCalendarModal from './components/app/StreakCalendarModal';
import { RiMenu2Line, RiCloseLine as RiCloseIcon } from 'react-icons/ri';
import KitsuMark from './components/brand/KitsuMark';
import BlobLab from './pages/dev/BlobLab';
import Tour from './components/tour/Tour';
import { useTour } from './components/tour/useTour';
import { TourContext } from './components/tour/TourContext';
import CampaignManager from './pages/admin/CampaignManager';
import FeedbackManager from './pages/admin/FeedbackManager';

/* ── Route guards ────────────────────────────────────────────── */
function RequireUser({ children }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/admin" replace />;
}

/* ── Layouts ─────────────────────────────────────────────────── */
function KitsuAtmosphere() {
  const glyphs = ['あ', '文', '日', '火', '学', '道', '語', '一', '心', '月', '水', '本', '山', '川', '空', '人', '年', '先', '名', '食'];
  return (
    <div className="kitsu-atmosphere" aria-hidden="true">
      {glyphs.map((glyph, i) => <span key={`${glyph}-${i}`}>{glyph}</span>)}
    </div>
  );
}


/* Kana reads its script, mode, groups and stage from the query string
   exactly once, in a useMemo with no dependencies — deliberately, so that
   changing script inside the page cannot rewrite the lesson it was
   launched with. The cost is that navigating from one stage to the next
   changes the URL and nothing else: same component instance, same frozen
   params, so "next step" replayed the stage just finished.

   Keying on the search string remounts it, which is what a new stage
   actually is. */
function KeyedKana() {
  const { search } = useLocation();
  return <Kana key={search} />;
}

function AppShell({ children }) {

  const { logout, user, updateUser } = useAuth();
  const location = useLocation();
  /* First-run tour. Account settings replays it through the tour bus. */
  const tour = useTour(user?._id);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [streakCalOpen, setStreakCalOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => {
    try { return localStorage.getItem('kitsu-app-sidebar-collapsed') === 'true'; }
    catch (_) { return false; }
  });

  React.useEffect(() => {
    try { localStorage.setItem('kitsu-app-sidebar-collapsed', String(isSidebarCollapsed)); }
    catch (_) {}
  }, [isSidebarCollapsed]);

  // The streak pill (and the dashboard) read user.streak/lastStudied from
  // AuthContext, which is only populated at login/getMe — it never sees a
  // streak earned mid-session from grading a card. Route changes are a
  // reliable, cheap proxy for "the user probably just finished something",
  // so resync here rather than needing every single grading call site
  // (kana/kanji/vocab progress, quiz, practice tests, grammar) to know
  // about AuthContext.
  React.useEffect(() => {
    getStreakCalendar()
      .then(res => updateUser({ streak: res.data.streak, lastStudied: res.data.lastStudied }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <AppLevelProvider user={user}>
    <div
      className="app-shell"
      style={{
        display: 'flex',
        minHeight: '100vh',
        /* Bottom-pinned UI (the exercise footer) is position:fixed and so
           cannot see the flex layout — it reads this to keep clear of the
           sidebar. The mobile breakpoint overrides it to 0, where the
           sidebar becomes a drawer. */
        '--app-sidebar-w': `${isSidebarCollapsed ? 68 : 252}px`,
      }}
    >
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <button
            className="mobile-nav-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle Menu"
            style={{ flexShrink: 0 }}
          >
            {isSidebarOpen ? <RiCloseIcon size={24} /> : <RiMenu2Line size={24} />}
          </button>
          <KitsuMark size={26} style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 900, fontSize: 18, whiteSpace: 'nowrap' }}>Kitsu<span style={{ color: '#f97316' }}>Speak</span></span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <AnnouncementBell />
          {/* The same [data-tour] hook the desktop topbar carries. Below
              1024px that topbar is display:none, so without this the tour
              found the hidden one, measured a 0x0 box and gave up on the
              spotlight — the streak step explained a pill it never lit. */}
          <span data-tour="streak">
            <StreakPill streak={user?.streak ?? 0} lastStudied={user?.lastStudied} onClick={() => setStreakCalOpen(true)} />
          </span>
        </div>
      </header>

      {streakCalOpen && (
        <StreakCalendarModal lastStudied={user?.lastStudied} onClose={() => setStreakCalOpen(false)} />
      )}

      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      <div
        className={`app-sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}
        style={{
          width: isSidebarCollapsed ? 68 : 252,
          flexShrink: 0,
          transition: 'width 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <AppSidebar
          onLogout={logout}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(value => !value)}
        />
      </div>

      <main className="app-main" style={{ flex: 1, overflowY: 'auto', background: '#10191d' }}>
        <KitsuAtmosphere />
        <AppTopbar user={user} onOpenStreak={() => setStreakCalOpen(true)} />
        {tour.open && <Tour onClose={tour.finish} setSidebarOpen={setIsSidebarOpen} />}
        {/* Scoped to the page so a crash leaves the sidebar and topbar
            usable, and keyed on the path so navigating away clears it —
            a boundary that has caught stays caught until it remounts. */}
        <ErrorBoundary key={location.pathname}>
          {/* Pages the tour points at need to know it is running: the
              learn path keeps its active section expanded while one is,
              because a collapsed section has no box to spotlight. */}
          <TourContext.Provider value={tour.open}>
            {children}
          </TourContext.Provider>
        </ErrorBoundary>
      </main>

      <MobileBottomNav onOpenMore={() => setIsSidebarOpen(true)} />
    </div>
    </AppLevelProvider>
  );
}

function AdminShell({ children }) {
  const { adminLogout } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => {
    try { return localStorage.getItem('kitsu-admin-sidebar-collapsed') === 'true'; }
    catch (_) { return false; }
  });

  React.useEffect(() => {
    try { localStorage.setItem('kitsu-admin-sidebar-collapsed', String(isSidebarCollapsed)); }
    catch (_) {}
  }, [isSidebarCollapsed]);
  
  return (
    <div
      className="app-shell"
      style={{
        display: 'flex',
        minHeight: '100vh',
        /* Bottom-pinned UI (the exercise footer) is position:fixed and so
           cannot see the flex layout — it reads this to keep clear of the
           sidebar. The mobile breakpoint overrides it to 0, where the
           sidebar becomes a drawer. */
        '--app-sidebar-w': `${isSidebarCollapsed ? 68 : 252}px`,
      }}
    >
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <button
            className="mobile-nav-toggle"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle Menu"
            style={{ flexShrink: 0 }}
          >
            {isSidebarOpen ? <RiCloseIcon size={24} /> : <RiMenu2Line size={24} />}
          </button>
          <KitsuMark size={26} style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 900, fontSize: 18, whiteSpace: 'nowrap' }}>Kitsu<span style={{ color: '#f97316' }}>Speak</span> Admin</span>
        </div>
      </header>

      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
      />

      <div
        className={`app-sidebar-wrapper ${isSidebarOpen ? 'open' : ''}`}
        style={{
          width: isSidebarCollapsed ? 66 : 224,
          flexShrink: 0,
          transition: 'width 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <AdminSidebar
          onLogout={adminLogout}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(value => !value)}
        />
      </div>

      <main className="app-main" style={{ flex: 1, overflowY: 'auto', background: '#10191d' }}>
        <KitsuAtmosphere />
        <ErrorBoundary key={location.pathname}>
          {children}
        </ErrorBoundary>
      </main>
    </div>
  );
}

/* ── All routes ──────────────────────────────────────────────── */
function AppRoutes() {
  const { isAdmin } = useAuth();

  return (
    <Routes>
      {/* ── Public ─────────────────────────────────────────── */}
      <Route path="/"       element={<Landing />} />
      <Route path="/login"  element={<Auth mode="login"  />} />
      <Route path="/signup" element={<Auth mode="signup" />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* ── User app ───────────────────────────────────────── */}
      {/* The learning path is the app's entry point; the old dashboard
          now lives at /profile. /roadmap showed the same chapter data as
          the path, so it redirects rather than offering two views of it. */}
      <Route path="/dashboard"  element={<RequireUser><AppShell><PageGate pageKey="learn"><LearnPath /></PageGate></AppShell></RequireUser>} />
      <Route path="/profile"    element={<RequireUser><AppShell><PageGate pageKey="profile"><Profile   /></PageGate></AppShell></RequireUser>} />
      <Route path="/roadmap"    element={<Navigate to="/dashboard" replace />} />
      {/* Deliberately not behind a PageGate: if the profile page is ever put
          under maintenance, a learner must still be able to change their
          password or close their account. */}
      <Route path="/settings"   element={<RequireUser><AppShell><AccountSettings /></AppShell></RequireUser>} />
      <Route path="/kana"       element={<RequireUser><AppShell><PageGate pageKey="kana"><KeyedKana /></PageGate></AppShell></RequireUser>} />
      <Route path="/flashcards" element={<RequireUser><AppShell><PageGate pageKey="flashcards"><Flashcards /></PageGate></AppShell></RequireUser>} />
      <Route path="/quiz"       element={<RequireUser><AppShell><PageGate pageKey="quiz"><Quiz       /></PageGate></AppShell></RequireUser>} />
      <Route path="/grammar"            element={<RequireUser><AppShell><PageGate pageKey="grammar"><GrammarChapters /></PageGate></AppShell></RequireUser>} />
      <Route path="/grammar/:number"    element={<RequireUser><AppShell><PageGate pageKey="grammar"><GrammarChapter  /></PageGate></AppShell></RequireUser>} />
      <Route path="/kanji"      element={<RequireUser><AppShell><PageGate pageKey="kanji"><Kanji      /></PageGate></AppShell></RequireUser>} />
      <Route path="/practice-tests" element={<RequireUser><AppShell><PageGate pageKey="practice-tests"><PracticeTests /></PageGate></AppShell></RequireUser>} />
      <Route path="/practice-tests/:testId/take" element={<RequireUser><PracticeTestTake /></RequireUser>} />
      <Route path="/practice-tests/results/:attemptId" element={<RequireUser><AppShell><PageGate pageKey="practice-tests"><PracticeTestResults /></PageGate></AppShell></RequireUser>} />
      <Route path="/practice-tests/history" element={<RequireUser><AppShell><PageGate pageKey="practice-tests"><PracticeTestHistory /></PageGate></AppShell></RequireUser>} />

      {/* ── Admin ──────────────────────────────────────────── */}
      <Route path="/admin" element={
        isAdmin ? <Navigate to="/admin/overview" replace /> : <AdminLogin />
      } />
      <Route path="/admin/overview"       element={<RequireAdmin><AdminShell><Overview             /></AdminShell></RequireAdmin>} />
      <Route path="/admin/vocabulary"     element={<RequireAdmin><AdminShell><VocabularyManager    /></AdminShell></RequireAdmin>} />
      <Route path="/admin/kana"           element={<RequireAdmin><AdminShell><KanaManager          /></AdminShell></RequireAdmin>} />
      <Route path="/admin/kanji"          element={<RequireAdmin><AdminShell><KanjiManager         /></AdminShell></RequireAdmin>} />
      <Route path="/admin/grammar"        element={<RequireAdmin><AdminShell><GrammarChapterManager /></AdminShell></RequireAdmin>} />
      <Route path="/admin/quiz"           element={<RequireAdmin><AdminShell><QuizManager          /></AdminShell></RequireAdmin>} />
      <Route path="/admin/users"          element={<RequireAdmin><AdminShell><UsersManager         /></AdminShell></RequireAdmin>} />
      <Route path="/admin/analytics"      element={<RequireAdmin><AdminShell><Analytics            /></AdminShell></RequireAdmin>} />
      <Route path="/admin/settings"       element={<RequireAdmin><AdminShell><SiteSettings         /></AdminShell></RequireAdmin>} />
      <Route path="/admin/announcements"  element={<RequireAdmin><AdminShell><AnnouncementsManager /></AdminShell></RequireAdmin>} />
      <Route path="/admin/campaign"       element={<RequireAdmin><AdminShell><CampaignManager     /></AdminShell></RequireAdmin>} />
      <Route path="/admin/feedback"       element={<RequireAdmin><AdminShell><FeedbackManager     /></AdminShell></RequireAdmin>} />
      <Route path="/admin/practice-tests" element={<RequireAdmin><AdminShell><PracticeTestManager /></AdminShell></RequireAdmin>} />

      {/* ── 404 ────────────────────────────────────────────── */}
      {/* Scratch page for the mark's animations. Compiled out of
          production builds, so the route does not exist there. */}
      {process.env.NODE_ENV === 'development' && (
        <Route path="/__blob" element={<BlobLab />} />
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/* ── Root ────────────────────────────────────────────────────── */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <ConfigProvider>
        <AppRoutes />
        </ConfigProvider>
    </AuthProvider>
    </BrowserRouter>
  );
}
